import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { getSilentRedirectUri } from '../utils/msal-config';

export interface EntraBranding {
  bannerLogoUrl: string | null;
  squareLogoUrl: string | null;
  backgroundImageUrl: string | null;
}

/**
 * Solo carga la foto de perfil del usuario, que requiere User.Read.
 *
 * El branding por organización (logo y fondo del tenant) se retiró: la API
 * /organization/{tid}/branding exige Organization.Read.All, un permiso que solo puede aprobar
 * un administrador y que no está registrado en la app. En el modelo multi-tenant obligaría a
 * cada cliente a autorizar la lectura de los datos de su organización. El tipo EntraBranding y
 * la señal que lo consume se conservan por si se retoma con consentimiento de administrador.
 */
@Injectable({
  providedIn: 'root'
})
export class EntraBrandingService {
  private readonly msalService = inject(MsalService, { optional: true });
  private readonly objectUrls: string[] = [];

  async loadProfilePhoto(): Promise<string | null> {
    if (!this.msalService) {
      return null;
    }

    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0]
      ?? null;
    if (!account) {
      return null;
    }

    const accessToken = await this.acquireGraphToken(account);
    if (!accessToken) {
      return null;
    }

    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (!response.ok) {
        return null;
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        return null;
      }

      const objectUrl = URL.createObjectURL(blob);
      this.objectUrls.push(objectUrl);
      return objectUrl;
    } catch {
      return null;
    }
  }

  private async acquireGraphToken(
    account: NonNullable<ReturnType<NonNullable<MsalService['instance']>['getActiveAccount']>>
  ): Promise<string> {
    try {
      const result = await this.msalService!.instance.acquireTokenSilent({
        account,
        scopes: ['User.Read'],
        redirectUri: getSilentRedirectUri()
      });

      return result.accessToken ?? '';
    } catch (error) {
      console.info('[Entra Branding] No se pudo obtener token de Graph para la foto de perfil.', error);
      return '';
    }
  }

  clear(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls.length = 0;
  }
}
